/**
 * Supplier API Service
 * Handles all supplier-related API operations
 */

import { apiConfig, makeApiRequest } from './apiConfig';

/**
 * Supplier interface for API responses
 */
export interface SupplierApiResponse {
  _id?: string;
  id?: string | number;
  companyName: string;
  name?: string;
  taxId: string;
  registrationNumber?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  contactPersons?: Array<{
    name: string;
    role: string;
    email: string;
    phone: string;
    isPrimary: boolean;
  }>;
  contactPerson?: string;
  email?: string;
  phone?: string;
  performanceRating?: number;
  verificationStatus?: string;
  category?: string;
  status?: string;
  contractCount?: number;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Frontend Supplier interface
 */
export interface FrontendSupplier {
  id: string | number;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  category: string;
  status: string;
  taxId?: string;
  registrationNumber?: string;
  performanceRating?: number;
  contractCount?: number;
}

/**
 * Supplier Service class
 */
class SupplierService {
  /**
   * Get all suppliers
   * @param {string} token - Auth token
   * @param {object} filters - Optional filters
   * @returns {Promise<FrontendSupplier[]>} List of suppliers
   */
  async getAll(token: string, filters: Record<string, any> = {}): Promise<FrontendSupplier[]> {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const url = queryParams 
        ? `${apiConfig.endpoints.suppliers.getAll}?${queryParams}`
        : apiConfig.endpoints.suppliers.getAll;
      
      const response = await makeApiRequest<SupplierApiResponse[]>(url, { method: 'GET' }, token);
      const suppliers = response.data || [];
      
      // Transform all suppliers to frontend format
      return suppliers.map(supplier => this.transformToFrontendFormat(supplier));
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      throw error;
    }
  }

  /**
   * Get single supplier by ID
   * @param {string} token - Auth token
   * @param {string} id - Supplier ID
   * @returns {Promise<FrontendSupplier>} Supplier data
   */
  async getById(token: string, id: string): Promise<FrontendSupplier> {
    try {
      const response = await makeApiRequest<SupplierApiResponse>(
        apiConfig.endpoints.suppliers.getOne(id),
        { method: 'GET' },
        token
      );
      return this.transformToFrontendFormat(response.data!);
    } catch (error) {
      console.error(`Error fetching supplier ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create new supplier
   * @param {string} token - Auth token
   * @param {FrontendSupplier} supplierData - Supplier data
   * @returns {Promise<FrontendSupplier>} Created supplier
   */
  async create(token: string, supplierData: FrontendSupplier): Promise<FrontendSupplier> {
    try {
      // Transform frontend supplier format to backend format
      const backendData = this.transformToBackendFormat(supplierData);
      
      const response = await makeApiRequest<SupplierApiResponse>(
        apiConfig.endpoints.suppliers.create,
        {
          method: 'POST',
          body: JSON.stringify(backendData)
        },
        token
      );
      
      // Transform backend response to frontend format
      return this.transformToFrontendFormat(response.data!);
    } catch (error) {
      console.error('Error creating supplier:', error);
      throw error;
    }
  }

  /**
   * Update existing supplier
   * @param {string} token - Auth token
   * @param {string} id - Supplier ID
   * @param {FrontendSupplier} updates - Supplier updates
   * @returns {Promise<FrontendSupplier>} Updated supplier
   */
  async update(token: string, id: string, updates: FrontendSupplier): Promise<FrontendSupplier> {
    try {
      // Transform frontend supplier format to backend format
      const backendData = this.transformToBackendFormat(updates);
      
      const response = await makeApiRequest<SupplierApiResponse>(
        apiConfig.endpoints.suppliers.update(id),
        {
          method: 'PUT',
          body: JSON.stringify(backendData)
        },
        token
      );
      
      // Transform backend response to frontend format
      return this.transformToFrontendFormat(response.data!);
    } catch (error) {
      console.error(`Error updating supplier ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete supplier
   * @param {string} token - Auth token
   * @param {string} id - Supplier ID
   * @returns {Promise<boolean>} Success status
   */
  async delete(token: string, id: string): Promise<boolean> {
    try {
      await makeApiRequest(
        apiConfig.endpoints.suppliers.delete(id),
        { method: 'DELETE' },
        token
      );
      return true;
    } catch (error) {
      console.error(`Error deleting supplier ${id}:`, error);
      throw error;
    }
  }

  /**
   * Search suppliers
   * @param {string} token - Auth token
   * @param {string} query - Search query
   * @returns {Promise<FrontendSupplier[]>} Search results
   */
  async search(token: string, query: string): Promise<FrontendSupplier[]> {
    try {
      const response = await makeApiRequest<SupplierApiResponse[]>(
        `${apiConfig.endpoints.suppliers.search}?q=${encodeURIComponent(query)}`,
        { method: 'GET' },
        token
      );
      
      // Transform all results to frontend format
      return (response.data || []).map(supplier => 
        this.transformToFrontendFormat(supplier)
      );
    } catch (error) {
      console.error('Error searching suppliers:', error);
      throw error;
    }
  }

  /**
   * Get supplier contracts
   * @param {string} token - Auth token
   * @param {string} supplierId - Supplier ID
   * @returns {Promise<any[]>} List of contracts
   */
  async getContracts(token: string, supplierId: string): Promise<any[]> {
    try {
      const response = await makeApiRequest<any[]>(
        apiConfig.endpoints.suppliers.getContracts(supplierId),
        { method: 'GET' },
        token
      );
      return response.data || [];
    } catch (error) {
      console.error(`Error fetching contracts for supplier ${supplierId}:`, error);
      throw error;
    }
  }

  /**
   * Transform frontend supplier format to backend format
   * @param {FrontendSupplier} frontendSupplier - Supplier in frontend format
   * @returns {object} Supplier in backend format
   */
  transformToBackendFormat(frontendSupplier: any): any {
    // Handle both new structured format and legacy format
    let address: any = {};
    
    if (frontendSupplier.address) {
      if (typeof frontendSupplier.address === 'string') {
        // Legacy string address - parse it
        const parts = frontendSupplier.address.split(',').map((p: string) => p.trim());
        address = {
          street: parts[0] || '',
          city: parts[1] || '',
          state: parts[2] || '',
          zipCode: parts[3] || '',
          country: parts[4] || 'USA'
        };
      } else if (typeof frontendSupplier.address === 'object') {
        // New structured address format
        address = {
          street: frontendSupplier.address.street || '',
          city: frontendSupplier.address.city || '',
          state: frontendSupplier.address.state || '',
          zipCode: frontendSupplier.address.zipCode || '',
          country: frontendSupplier.address.country || 'USA'
        };
      }
    }

    // Handle contact persons - prioritize new structured format
    let contactPersons: any[] = [];
    if (frontendSupplier.contactPersons && Array.isArray(frontendSupplier.contactPersons)) {
      // New structured format
      contactPersons = frontendSupplier.contactPersons;
    } else if (frontendSupplier.contactPerson || frontendSupplier.email) {
      // Legacy format - create contact person from individual fields
      contactPersons = [{
        name: frontendSupplier.contactPerson || '',
        role: 'Primary Contact',
        email: frontendSupplier.email || '',
        phone: frontendSupplier.phone || '',
        isPrimary: true
      }];
    }

    return {
      companyName: frontendSupplier.companyName || frontendSupplier.name,
      taxId: frontendSupplier.taxId || `TAX${Date.now()}`,
      registrationNumber: frontendSupplier.registrationNumber,
      address: address,
      contactPersons: contactPersons,
      performanceRating: frontendSupplier.performanceRating,
      verificationStatus: this.mapStatusToVerification(frontendSupplier.status),
      category: frontendSupplier.category,
      createdBy: frontendSupplier.createdBy
    };
  }

  /**
   * Transform backend supplier format to frontend format
   * @param {SupplierApiResponse} backendSupplier - Supplier in backend format
   * @returns {FrontendSupplier} Supplier in frontend format
   */
  transformToFrontendFormat(backendSupplier: SupplierApiResponse): FrontendSupplier {
    const primaryContact = backendSupplier.contactPersons?.find(c => c.isPrimary) 
                          || backendSupplier.contactPersons?.[0];
    
    return {
      id: backendSupplier._id || backendSupplier.id || '',
      name: backendSupplier.companyName || backendSupplier.name || '',
      contactPerson: primaryContact?.name || backendSupplier.contactPerson || '',
      email: primaryContact?.email || backendSupplier.email || '',
      phone: primaryContact?.phone || backendSupplier.phone || '',
      address: backendSupplier.address ? 
        `${backendSupplier.address.street}, ${backendSupplier.address.city}, ${backendSupplier.address.state}, ${backendSupplier.address.zipCode}, ${backendSupplier.address.country}` : '',
      category: backendSupplier.category || 'General',
      status: this.mapVerificationToStatus(backendSupplier.verificationStatus),
      taxId: backendSupplier.taxId,
      registrationNumber: backendSupplier.registrationNumber,
      performanceRating: backendSupplier.performanceRating,
      contractCount: backendSupplier.contractCount || 0
    };
  }

  /**
   * Map frontend status to backend verification status
   * @param {string} status - Frontend status
   * @returns {string} Backend verification status
   */
  mapStatusToVerification(status: string): string {
    const statusMap: Record<string, string> = {
      'active': 'verified',
      'inactive': 'pending',
      'suspended': 'suspended',
      'pending': 'pending'
    };
    return statusMap[status?.toLowerCase()] || 'pending';
  }

  /**
   * Map backend verification status to frontend status
   * @param {string | undefined} verificationStatus - Backend verification status
   * @returns {string} Frontend status
   */
  mapVerificationToStatus(verificationStatus: string | undefined): string {
    const statusMap: Record<string, string> = {
      'verified': 'active',
      'pending': 'pending',
      'suspended': 'inactive',
      'rejected': 'inactive'
    };
    return statusMap[verificationStatus?.toLowerCase() || ''] || 'pending';
  }
}

// Export singleton instance
export const supplierService = new SupplierService();

export default supplierService;
